import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { TrendingUp, TrendingDown, BarChart3, Calendar, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toLocaleString()}`;
};

const PORTFOLIO_COLORS = {
  bedrock: 'bg-emerald-100 text-emerald-700',
  fortress: 'bg-sky-100 text-sky-700',
  horizon: 'bg-indigo-100 text-indigo-700',
  velocity: 'bg-amber-100 text-amber-700',
  summit: 'bg-orange-100 text-orange-700',
  nebula: 'bg-purple-100 text-purple-700',
};

const SCENARIO_COLORS = {
  low_stress: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Low Stress' },
  normal: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Normal' },
  elevated: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Elevated' },
  high_stress: { bg: 'bg-red-100', text: 'text-red-700', label: 'High Stress' },
};

export default function PredictionsPage() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadPredictions = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/predictions`);
      if (res.ok) {
        const data = await res.json();
        setPredictions(data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    loadPredictions();
  }, []);

  const generatePredictions = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/generate-predictions`, { method: 'POST' });
      if (res.ok) {
        await loadPredictions();
      }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
    </div>
  );

  // Calculate summary stats
  const total7DayRed = predictions.reduce((s, p) => s + (p.forecast7Day?.expectedRedemptions || 0), 0);
  const total30DayRed = predictions.reduce((s, p) => s + (p.forecast30Day?.expectedRedemptions || 0), 0);
  const avgConfidence = predictions.length > 0 
    ? predictions.reduce((s, p) => s + (p.forecast7Day?.confidence || 0), 0) / predictions.length 
    : 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Liquidity Predictions</h1>
          <p className="text-sm text-gray-500 mt-1">
            AI-powered redemption forecasts for liquidity planning
          </p>
        </div>
        <Button onClick={generatePredictions} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700">
          {generating ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
          ) : (
            <><BarChart3 className="w-4 h-4 mr-2" /> Generate Predictions</>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-sky-600" />
              <p className="text-xs text-gray-500 uppercase">7-Day Forecast</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{fmt(total7DayRed)}</p>
            <p className="text-xs text-gray-500">Expected redemptions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-indigo-600" />
              <p className="text-xs text-gray-500 uppercase">30-Day Forecast</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{fmt(total30DayRed)}</p>
            <p className="text-xs text-gray-500">Expected redemptions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Model Confidence</p>
            <p className="text-2xl font-bold text-emerald-600">{(avgConfidence * 100).toFixed(0)}%</p>
            <p className="text-xs text-gray-500">Average across portfolios</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase">Portfolios Analyzed</p>
            <p className="text-2xl font-bold text-gray-900">{predictions.length}</p>
            <p className="text-xs text-gray-500">With active predictions</p>
          </CardContent>
        </Card>
      </div>

      {/* Predictions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Portfolio Redemption Forecasts</CardTitle>
          <CardDescription>Predicted redemption amounts and scenarios by portfolio</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Portfolio</TableHead>
                <TableHead className="text-right">7-Day Redemptions</TableHead>
                <TableHead className="text-right">30-Day Redemptions</TableHead>
                <TableHead>Scenario</TableHead>
                <TableHead className="text-right">Confidence</TableHead>
                <TableHead>Risk Level</TableHead>
                <TableHead>Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {predictions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No predictions generated. Click "Generate Predictions" to create forecasts.
                  </TableCell>
                </TableRow>
              ) : (
                predictions.map((pred) => {
                  const scenario = SCENARIO_COLORS[pred.forecast7Day?.scenario] || SCENARIO_COLORS.normal;
                  const confidence = pred.forecast7Day?.confidence || 0;
                  const expected7d = pred.forecast7Day?.expectedRedemptions || 0;
                  const isHighRisk = pred.recommendation?.urgency === 'HIGH' || pred.recommendation?.urgency === 'CRITICAL';
                  
                  return (
                    <TableRow key={pred.portfolioId} data-testid={`prediction-row-${pred.portfolioId}`}>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[pred.portfolioId] || 'bg-gray-100 text-gray-700'}`}>
                          {pred.portfolioId}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmt(pred.forecast7Day?.expectedRedemptions || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(pred.forecast30Day?.expectedRedemptions || 0)}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${scenario.bg} ${scenario.text}`}>
                          {scenario.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={confidence >= 0.7 ? 'text-emerald-600' : confidence >= 0.5 ? 'text-amber-600' : 'text-red-600'}>
                          {(confidence * 100).toFixed(0)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        {isHighRisk ? (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertTriangle className="w-3 h-3 mr-1" /> High
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" /> Low
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(pred.generatedAt || Date.now()).toLocaleString('en-AU')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recommendations */}
      {predictions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recommendations</CardTitle>
            <CardDescription>Action items from the prediction engine</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {predictions.map((pred) => (
                <div key={`rec-${pred.portfolioId}`} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[pred.portfolioId] || 'bg-gray-100 text-gray-700'}`}>
                      {pred.portfolioId}
                    </span>
                    <Badge className={
                      pred.recommendation?.urgency === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                      pred.recommendation?.urgency === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                      'bg-emerald-100 text-emerald-700'
                    }>
                      {pred.recommendation?.urgency || 'LOW'}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium">{pred.recommendation?.action || 'MAINTAIN'}</p>
                  <p className="text-xs text-gray-500 mt-1">{pred.recommendation?.rationale}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
              {/* Methodology Note */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <p className="text-sm font-medium text-gray-700">Prediction Methodology</p>
          <p className="text-xs text-gray-500 mt-1">
            Predictions are generated using historical flow patterns, seasonal factors, and market conditions.
            The model analyzes the past 30 days of redemption data to forecast future liquidity needs.
            Confidence scores indicate the reliability of each prediction based on data quality and pattern consistency.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
